import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TbPlus } from 'react-icons/tb';
import PageHeader from '../../components/misc/PageHeader';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import PotsPage from './PotsPage';
import ContributionPlansPage from '../sales/ContributionPlansPage';
import CategoriesPage from './CategoriesPage';
import BookYearsSettingsPage from './BookYearsSettingsPage';
import BudgetSettingsPage from './BudgetSettingsPage';
import CategorizationRulesSettingsPage from './CategorizationRulesSettingsPage';

export enum SettingsPageTab {
  SAVING_POTS = 'savingPots',
  CONTRIBUTIONS = 'contributions',
  CATEGORIES = 'categories',
  BOOK_YEARS = 'bookYears',
  BUDGET = 'budget',
  CATEGORIZATION_RULES = 'categorizationRules',
}

export default function SettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Set initial tab based on the current path or state
  const getInitialTab = (): SettingsPageTab => {
    if (location.state?.activeTab) {
      return location.state.activeTab;
    }

    if (location.pathname.includes('/financial/pots')) {
      return SettingsPageTab.SAVING_POTS;
    }
    if (location.pathname.includes('/financial/contribution-plans')) {
      return SettingsPageTab.CONTRIBUTIONS;
    }
    if (location.pathname.includes('/financial/categories')) {
      return SettingsPageTab.CATEGORIES;
    }
    if (location.pathname.includes('/financial/book-years')) {
      return SettingsPageTab.BOOK_YEARS;
    }
    if (location.pathname.includes('/financial/budget')) {
      return SettingsPageTab.BUDGET;
    }
    if (location.pathname.includes('/financial/categorization-rules')) {
      return SettingsPageTab.CATEGORIZATION_RULES;
    }
    return SettingsPageTab.SAVING_POTS;
  };

  const [activeTab, setActiveTab] = React.useState<SettingsPageTab>(getInitialTab());
  const [openContributionPlanCreateModal, setOpenContributionPlanCreateModal] = React.useState<(() => void) | null>(
    null,
  );

  React.useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const renderTabContent = () => {
    switch (activeTab) {
      case SettingsPageTab.SAVING_POTS:
        return <PotsPage hideHeader />;
      case SettingsPageTab.CONTRIBUTIONS:
        return (
          <ContributionPlansPage
            registerCreateModalOpener={(open) => setOpenContributionPlanCreateModal(() => open)}
          />
        );
      case SettingsPageTab.CATEGORIES:
        return <CategoriesPage hideHeader />;
      case SettingsPageTab.BOOK_YEARS:
        return <BookYearsSettingsPage hideHeader />;
      case SettingsPageTab.BUDGET:
        return <BudgetSettingsPage hideHeader />;
      case SettingsPageTab.CATEGORIZATION_RULES:
        return <CategorizationRulesSettingsPage hideHeader />;
      default:
        return <PotsPage hideHeader />;
    }
  };

  return (
    <>
      <div>
        <PageHeader
          title={t('page.financial.settings.title')}
          subtitle={t('page.financial.settings.subtitle')}
          {...(activeTab === SettingsPageTab.SAVING_POTS && {
            primaryButton: (
              <PrimaryButton
                text={t('page.financial.pots.addPot')}
                icon={TbPlus}
                onClick={() => navigate('/financial/pots/create')}
              />
            ),
          })}
          {...(activeTab === SettingsPageTab.CONTRIBUTIONS &&
            openContributionPlanCreateModal && {
              primaryButton: (
                <PrimaryButton
                  text={t('page.financial.contributionPlans.create')}
                  icon={TbPlus}
                  onClick={() => openContributionPlanCreateModal()}
                />
              ),
            })}
        />

        {/* Tabs */}
        <div className="mb-6 flex flex-wrap gap-4 border-b border-primary-300">
          {(Object.values(SettingsPageTab) as SettingsPageTab[]).map((tabKey) => (
            <button
              key={tabKey}
              type="button"
              onClick={() => setActiveTab(tabKey)}
              className={`relative px-4 pb-1 pt-2 font-medium transition-all duration-150 ${
                activeTab === tabKey
                  ? 'text-primary-700 -mb-[1px] rounded-t-md border border-primary-300 border-b-secondary-50'
                  : 'text-primary-600 hover:text-primary-800'
              }`}>
              {t(`page.financial.settings.tabs.${tabKey}`)}
            </button>
          ))}
        </div>

        <div className="w-full">{renderTabContent()}</div>
      </div>
    </>
  );
}